import React, { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';
import { Observer } from 'gsap/Observer';
import {
  SECTION_CHECKPOINTS,
  SECTION_LABELS,
  TOTAL_FRAMES,
  TRANSITION_DURATION,
  COOLDOWN_DURATION_MS,
  DEFAULT_SECTIONS,
  type FrameTrackState,
  type ScrollLockedAvianSequenceProps,
} from './avianSequence.types';
import { drawAvianFrame, startAvianPreload } from './avianPreloader';
import Hero from '../../features/hero/Hero';
import About from '../../features/about/About';
import Projects from '../../features/projects/Projects';
import Experience from '../../features/experience/Experience';
import Achievements from '../../features/achievements/Achievements';
import SequenceHUD from './SequenceHUD';
import { useSequenceHUDState } from './useSequenceHUDState';
import './MobileScrollLockedSequence.styles.css';

// * Register GSAP Observer plugin safely once
if (typeof window !== 'undefined') {
  gsap.registerPlugin(Observer);
}

// * [COMPONENT] Mobile Discrete Scroll-Locked Canvas Sequence Engine
// ? Renders portrait WebP sequence (/sequenceMobile/) synchronized to 5 core section checkpoints
export const MobileScrollLockedSequence: React.FC<ScrollLockedAvianSequenceProps> = ({
  onSectionChange,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  const [activeSection, setActiveSection] = useState<number>(0);

  const isAnimatingRef = useRef<boolean>(false);
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSectionRef = useRef<number>(0);
  const frameTrackRef = useRef<FrameTrackState>({ frame: 0 });
  const imagesRef = useRef<HTMLImageElement[]>([]);

  // * Unified HUD State (Recruiter Mode + Zoom Controls) across all 5 mobile sections
  const {
    scale,
    isRecruiterActive,
    handleZoomIn,
    handleZoomOut,
    handleRecruiterToggle,
  } = useSequenceHUDState(true);

  const isRecruiterActiveRef = useRef<boolean>(isRecruiterActive);
  useEffect(() => {
    isRecruiterActiveRef.current = isRecruiterActive;
  }, [isRecruiterActive]);

  // * Repaints current frame using high-DPI aspect cover math for mobile portrait ratios
  const renderFrame = useCallback((frameIndex: number) => {
    drawAvianFrame(
      canvasRef.current,
      imagesRef.current,
      frameIndex,
      TOTAL_FRAMES,
      false
    );
  }, []);

  // * High-DPI canvas resize observer handler
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    renderFrame(Math.round(frameTrackRef.current.frame));
  }, [renderFrame]);

  // * Discrete section transition controller with Master GSAP Timeline & Directional Parallax
  const goToSection = useCallback(
    (targetIndex: number) => {
      const maxAllowed = isRecruiterActiveRef.current ? SECTION_CHECKPOINTS.length - 1 : 2;
      if (
        targetIndex < 0 ||
        targetIndex > maxAllowed ||
        isAnimatingRef.current ||
        targetIndex === currentSectionRef.current
      ) {
        return;
      }

      // * Clear any pending cooldown timer
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
        cooldownTimeoutRef.current = null;
      }

      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current = null;
      }

      isAnimatingRef.current = true;
      const fromIndex = currentSectionRef.current;
      const toIndex = targetIndex;
      const direction = toIndex > fromIndex ? 1 : -1;
      const fromEl = sectionRefs.current[fromIndex];
      const toEl = sectionRefs.current[toIndex];
      const targetFrame = SECTION_CHECKPOINTS[targetIndex];
      const frameDelta = Math.abs(targetFrame - frameTrackRef.current.frame);

      const scrubDuration =
        frameDelta > 50
          ? Math.min(1.4, 1.15 + (frameDelta / 200) * 0.25)
          : TRANSITION_DURATION;

      // Ensure intermediate sections are completely hidden
      sectionRefs.current.forEach((el, idx) => {
        if (!el) return;
        if (idx !== fromIndex && idx !== toIndex) {
          gsap.set(el, { opacity: 0, pointerEvents: 'none', y: 0 });
        }
      });

      if (fromEl) {
        gsap.set(fromEl, { pointerEvents: 'none' });
      }
      if (toEl) {
        gsap.set(toEl, { opacity: 0, y: direction * 35, pointerEvents: 'none' });
      }

      const tl = gsap.timeline({
        onComplete: () => {
          currentSectionRef.current = targetIndex;
          setActiveSection(targetIndex);
          onSectionChange?.(targetIndex);

          if (toEl) {
            gsap.set(toEl, { opacity: 1, y: 0, pointerEvents: 'auto' });
          }

          // * 250ms trailing cooldown to discard inertial touch momentum
          cooldownTimeoutRef.current = setTimeout(() => {
            isAnimatingRef.current = false;
            timelineRef.current = null;
          }, COOLDOWN_DURATION_MS);
        },
      });

      timelineRef.current = tl;

      // 1. Outgoing section vanishes rapidly with directional translation
      if (fromEl) {
        tl.to(
          fromEl,
          {
            opacity: 0,
            y: -direction * 35,
            duration: 0.3,
            ease: 'power2.in',
          },
          0
        );
      }

      // 2. Avian frame scrubs smoothly
      tl.to(
        frameTrackRef.current,
        {
          frame: targetFrame,
          duration: scrubDuration,
          ease: 'power2.inOut',
          onUpdate: () => {
            const currentFrame = Math.round(frameTrackRef.current.frame);
            renderFrame(currentFrame);
          },
        },
        0.05
      );

      // 3. Incoming section enters and completes exactly as bird arrives
      if (toEl) {
        const entranceStart = Math.max(0.35, scrubDuration - 0.45);
        tl.to(
          toEl,
          {
            opacity: 1,
            y: 0,
            duration: 0.45,
            ease: 'power2.out',
          },
          entranceStart
        );
      }
    },
    [renderFrame, onSectionChange]
  );

  // * Initialize canvas resizing and progressive mobile frame preloading
  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initial state setup for section wrappers: 0 is visible, others hidden
    sectionRefs.current.forEach((el, idx) => {
      if (!el) return;
      if (idx === 0) {
        gsap.set(el, { opacity: 1, y: 0, pointerEvents: 'auto' });
      } else {
        gsap.set(el, { opacity: 0, y: 0, pointerEvents: 'none' });
      }
    });

    const { images, cleanup } = startAvianPreload(
      '/sequenceMobile',
      TOTAL_FRAMES,
      () => renderFrame(0)
    );
    imagesRef.current = images;

    return () => {
      cleanup();
      window.removeEventListener('resize', resizeCanvas);
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, [renderFrame, resizeCanvas]);

  // * Auto-snap back to Projects if recruiter mode becomes disabled while on Experience or Achievements
  useEffect(() => {
    if (!isRecruiterActive && currentSectionRef.current > 2) {
      goToSection(2);
    }
  }, [isRecruiterActive, goToSection]);

  // * GSAP Observer intercepts vertical swipe and touch gestures
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = Observer.create({
      target: containerRef.current,
      type: 'touch,wheel,pointer',
      preventDefault: true,
      tolerance: 15,
      onUp: () => {
        if (isAnimatingRef.current) return;
        const currentIdx = currentSectionRef.current;
        if (currentIdx > 0) {
          goToSection(currentIdx - 1);
        }
      },
      onDown: () => {
        if (isAnimatingRef.current) return;
        const currentIdx = currentSectionRef.current;
        const maxAllowed = isRecruiterActiveRef.current ? SECTION_CHECKPOINTS.length - 1 : 2;
        if (currentIdx < maxAllowed) {
          goToSection(currentIdx + 1);
        }
      },
    });

    // * Custom navigation event listener
    const handleNavigateSection = (e: Event) => {
      const detail = (e as CustomEvent)?.detail;
      const targetId = detail?.targetId;
      if (!targetId) return;

      const idx = DEFAULT_SECTIONS.findIndex((s) => s.id === targetId);
      if (idx !== -1) {
        if (!isRecruiterActiveRef.current && idx > 2) return;
        goToSection(idx);
      }
    };
    window.addEventListener('navigate-section', handleNavigateSection);

    return () => {
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
      observer.kill();
      window.removeEventListener('navigate-section', handleNavigateSection);
    };
  }, [goToSection]);

  return (
    <div
      ref={containerRef}
      className={`mobile-sequence-container ${className}`}
      data-testid="mobile-sequence-container"
    >
      {/* * Pinned Background Mobile WebP Canvas */}
      <canvas
        ref={canvasRef}
        className="mobile-sequence-canvas"
        data-testid="mobile-sequence-canvas"
      />

      {/* * Mobile Hero Section Full-Viewport Overlay (Section 0) */}
      <div
        ref={(el) => {
          sectionRefs.current[0] = el;
        }}
        className="mobile-hero-wrapper"
      >
        <Hero
          isActive={activeSection === 0}
          scale={scale}
          isRecruiterActive={isRecruiterActive}
        />
      </div>

      {/* * Mobile About Me Section Full-Viewport Overlay (Section 1) */}
      <div
        ref={(el) => {
          sectionRefs.current[1] = el;
        }}
        className="mobile-about-wrapper"
      >
        <About isActive={activeSection === 1} scale={scale} />
      </div>

      {/* * Mobile Projects Section Full-Viewport Overlay (Section 2) */}
      <div
        ref={(el) => {
          sectionRefs.current[2] = el;
        }}
        className="mobile-section-wrapper"
      >
        <Projects isActive={activeSection === 2} scale={scale} />
      </div>

      {/* * Mobile Experience Section Full-Viewport Overlay (Section 3) */}
      <div
        ref={(el) => {
          sectionRefs.current[3] = el;
        }}
        className="mobile-section-wrapper"
      >
        <Experience isActive={activeSection === 3} scale={scale} />
      </div>

      {/* * Mobile Achievements Section Full-Viewport Overlay (Section 4) */}
      <div
        ref={(el) => {
          sectionRefs.current[4] = el;
        }}
        className="mobile-section-wrapper"
      >
        <Achievements isActive={activeSection === 4} scale={scale} />
      </div>

      {/* * Unified Sequence HUD: Recruiter Mode (Top-Right) & Zoom Controls (Bottom-Right) on all mobile sections */}
      <SequenceHUD
        scale={scale}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        isRecruiterActive={isRecruiterActive}
        onRecruiterToggle={handleRecruiterToggle}
        showRecruiterTooltip={!isRecruiterActive && activeSection === 2}
      />

      {/* * Touch Navigation Dots with Labels (Right Pinned) */}
      <nav className="mobile-dots-nav" aria-label="Mobile section sequence navigation">
        {SECTION_CHECKPOINTS.map((_, idx) => {
          if (!isRecruiterActive && idx > 2) {
            return null;
          }
          const isActive = activeSection === idx;
          const label = SECTION_LABELS[idx] || `Section ${idx + 1}`;
          return (
            <div
              key={idx}
              className={`mobile-dot-item ${isActive ? 'is-active' : ''}`}
            >
              <button
                type="button"
                className={`mobile-dot-btn ${isActive ? 'is-active' : ''}`}
                aria-label={`Jump to ${label}`}
                aria-current={isActive ? 'true' : 'false'}
                onClick={() => goToSection(idx)}
              />
              <span className={`mobile-dot-label ${isActive ? 'is-active' : ''}`}>
                {label}
              </span>
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default MobileScrollLockedSequence;
